import { z } from "zod";

const comparisonOperatorSchema = z.enum(["eq", "gte", "lte"]);
const stringOperatorSchema = z.enum(["eq", "contains"]);

const numericFilterSchema = z.object({
    value: z.number(),
    operator: comparisonOperatorSchema.default("eq"),
});

const ratingFilterSchema = z.object({
    value: z.enum(["1", "2", "3", "4", "5"]),
    operator: comparisonOperatorSchema.default("eq"),
});

const stringFilterSchema = z.object({
    value: z.string(),
    operator: stringOperatorSchema.default("contains"),
});

export const searchBreedsSchema = z.object({
    name: stringFilterSchema.optional(),
    size: z.enum(["toy", "small", "medium", "large", "giant"]).optional(),
    weightMinLbs: numericFilterSchema.optional(),
    weightMaxLbs: numericFilterSchema.optional(),
    heightMinInches: numericFilterSchema.optional(),
    heightMaxInches: numericFilterSchema.optional(),
    lifespanMinYears: numericFilterSchema.optional(),
    lifespanMaxYears: numericFilterSchema.optional(),
    coatType: z
        .enum([
            "hairless",
            "smooth",
            "short",
            "medium",
            "long",
            "wire",
            "curly",
            "corded",
            "double",
        ])
        .optional(),
    hasDoubleCoat: z.boolean().optional(),
    sheddingLevel: z
        .enum(["none", "minimal", "moderate", "heavy", "seasonal_blowout"])
        .optional(),
    hypoallergenic: z.boolean().optional(),
    homeCompatibility: z
        .array(z.enum(["apartment", "condo", "house", "acreage"]))
        .optional(),
    minYardSize: z.enum(["none", "small", "large"]).optional(),
    urbanTolerance: z.enum(["low", "moderate", "high"]).optional(),
    maxHoursAloneDaily: numericFilterSchema.optional(),
    needsConsistentSchedule: z.boolean().optional(),
    separationAnxietyRisk: ratingFilterSchema.optional(),
    exerciseMinutesDaily: numericFilterSchema.optional(),
    exerciseIntensity: ratingFilterSchema.optional(),
    mentalStimulationNeeds: ratingFilterSchema.optional(),
    needsJob: z.boolean().optional(),
    trainability: ratingFilterSchema.optional(),
    intelligence: ratingFilterSchema.optional(),
    independence: ratingFilterSchema.optional(),
    handlerSensitivity: ratingFilterSchema.optional(),
    friendlinessStrangers: ratingFilterSchema.optional(),
    friendlinessDogs: ratingFilterSchema.optional(),
    friendlinessChildren: ratingFilterSchema.optional(),
    friendlinessCats: ratingFilterSchema.optional(),
    preyDrive: ratingFilterSchema.optional(),
    barkingTendency: ratingFilterSchema.optional(),
    howlingTendency: ratingFilterSchema.optional(),
    isAlertBarker: z.boolean().optional(),
    energyLevel: ratingFilterSchema.optional(),
    calmness: ratingFilterSchema.optional(),
    confidence: ratingFilterSchema.optional(),
    playfulness: ratingFilterSchema.optional(),
    affectionLevel: ratingFilterSchema.optional(),
    healthIssuesRisk: ratingFilterSchema.optional(),
    isBrachycephalic: z.boolean().optional(),
    heatTolerance: ratingFilterSchema.optional(),
    coldTolerance: ratingFilterSchema.optional(),
    recommendedExperience: z
        .enum([
            "first_time_friendly",
            "some_experience",
            "experienced",
            "expert_only",
        ])
        .optional(),
    forgivenessLevel: ratingFilterSchema.optional(),
    offLeashReliabilityPotential: ratingFilterSchema.optional(),
    puppyDifficulty: ratingFilterSchema.optional(),
    adultAdoptionSuitability: ratingFilterSchema.optional(),
    seniorCareDifficulty: ratingFilterSchema.optional(),
    carRideTolerance: ratingFilterSchema.optional(),
    boardingEase: ratingFilterSchema.optional(),
    airTravelSuitable: z.boolean().optional(),
    handlingTolerance: ratingFilterSchema.optional(),
    groomingFrequencyWeekly: numericFilterSchema.optional(),
    professionalGroomingWeeks: numericFilterSchema.optional(),
    foodCostMonthlyMin: numericFilterSchema.optional(),
    foodCostMonthlyMax: numericFilterSchema.optional(),
    groomingCostMonthly: numericFilterSchema.optional(),
    insuranceCostMonthly: numericFilterSchema.optional(),
    limit: z.number().int().positive().max(100).default(50),
    offset: z.number().int().nonnegative().default(0),
});

export type SearchBreedsInput = z.infer<typeof searchBreedsSchema>;
